import { extendTheme } from '@mui/joy/styles';

const theme = extendTheme({
  colorSchemes: {
    dark: {
      palette: {
        primary: {
          50: '#fafafa',
          100: '#f5f5f5',
          200: '#eeeeee',
          300: '#e0e0e0',
          400: '#bdbdbd',
          500: '#9e9e9e',
          600: '#757575',
          700: '#616161',
          800: '#424242',
          900: '#212121',
          solidBg: '#ffffff',
          solidColor: '#000000',
          solidHoverBg: '#f5f5f5',
          softBg: 'rgba(255, 255, 255, 0.1)',
          softColor: '#ffffff',
          softHoverBg: 'rgba(255, 255, 255, 0.15)',
          softActiveBg: 'rgba(255, 255, 255, 0.2)',
          outlinedBg: 'transparent',
          outlinedColor: '#ffffff',
          outlinedBorder: 'rgba(255, 255, 255, 0.3)',
          outlinedHoverBg: 'rgba(255, 255, 255, 0.05)',
          outlinedActiveBg: 'rgba(255, 255, 255, 0.1)',
        },
        success: {
          50: '#e8f5e8',
          100: '#c8e6c9',
          200: '#a5d6a7',
          300: '#81c784',
          400: '#66bb6a',
          500: '#4caf50',
          600: '#43a047',
          700: '#388e3c',
          800: '#2e7d32',
          900: '#1b5e20',
          solidBg: '#4caf50',
          solidColor: '#ffffff',
          solidHoverBg: '#43a047',
          softBg: 'rgba(76, 175, 80, 0.1)',
          softColor: '#66bb6a',
          softHoverBg: 'rgba(76, 175, 80, 0.15)',
        },
        warning: {
          50: '#fff3e0',
          100: '#ffe0b2',
          200: '#ffcc80',
          300: '#ffb74d',
          400: '#ffa726',
          500: '#ff9800',
          600: '#fb8c00',
          700: '#f57c00',
          800: '#ef6c00',
          900: '#e65100',
          solidBg: '#ff9800',
          solidColor: '#ffffff',
          solidHoverBg: '#fb8c00',
          softBg: 'rgba(255, 152, 0, 0.1)',
          softColor: '#ffb74d',
          softHoverBg: 'rgba(255, 152, 0, 0.15)',
        },
        danger: {
          50: '#ffebee',
          100: '#ffcdd2',
          200: '#ef9a9a',
          300: '#e57373',
          400: '#ef5350',
          500: '#f44336',
          600: '#e53935',
          700: '#d32f2f',
          800: '#c62828',
          900: '#b71c1c',
          solidBg: '#f44336',
          solidColor: '#ffffff',
          solidHoverBg: '#e53935',
          softBg: 'rgba(244, 67, 54, 0.1)',
          softColor: '#ef5350',
          softHoverBg: 'rgba(244, 67, 54, 0.15)',
        },
        neutral: {
          50: '#fafafa',
          100: '#f5f5f5',
          200: '#eeeeee',
          300: '#e0e0e0',
          400: '#bdbdbd',
          500: '#9e9e9e',
          600: '#757575',
          700: '#616161',
          800: '#424242',
          900: '#212121',
          solidBg: '#616161',
          solidColor: '#ffffff',
          solidHoverBg: '#757575',
          softBg: 'rgba(158, 158, 158, 0.1)',
          softColor: '#bdbdbd',
          softHoverBg: 'rgba(158, 158, 158, 0.15)',
        },
        background: {
          body: '#0a0a0a',
          surface: '#1a1a1a',
          popup: '#000000',
          level1: 'rgba(255, 255, 255, 0.05)',
          level2: 'rgba(255, 255, 255, 0.08)',
          level3: 'rgba(255, 255, 255, 0.12)',
        },
        text: {
          primary: '#ffffff',
          secondary: 'rgba(255, 255, 255, 0.7)',
          tertiary: 'rgba(255, 255, 255, 0.5)',
          icon: 'rgba(255, 255, 255, 0.8)',
        },
        divider: 'rgba(255, 255, 255, 0.12)',
      },
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    'title-lg': {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    'title-md': {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.4,
    },
    'title-sm': {
      fontWeight: 600,
      fontSize: '0.875rem',
      lineHeight: 1.4,
    },
    'body-lg': {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    'body-md': {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    'body-sm': {
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    'body-xs': {
      fontSize: '0.625rem',
      lineHeight: 1.5,
    },
  },
  components: {
    JoyButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          fontWeight: 600,
          textTransform: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    JoyCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    JoySheet: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          backdropFilter: 'blur(10px)',
        },
      },
    },
    JoyInput: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          '&:focus-within': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
            boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },
    JoyIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        },
      },
    },
    JoyChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 500,
        },
      },
    },
  },
  focus: {
    default: {
      outlineColor: 'rgba(255, 255, 255, 0.5)',
      outlineWidth: '2px',
    },
  },
  shadow: {
    xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
  },
});

export default theme; 